import { Platform } from 'quasar';
import { useGlobalStore } from 'src/stores/useGlobalStore';
import { useOnlineStore } from 'src/stores/useOnlineStore';
import { useUserStore } from 'src/stores/useUserStore';
import { axiosApi } from 'src/utils/axios';
import { onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/**
 * Tells the server this client is alive, and what it is running.
 *
 * Presence used to be inferred from a websocket membership list, which counts
 * sockets rather than people: a second tab made one person two arrivals, and
 * closing either one announced a departure while they were still working. It
 * also never revisited what it learned at login, so a director watched versions
 * and page names that were hours old.
 *
 * A beat carries the whole truth about this client, so the server never has to
 * assume anything survived from last time.
 */

// One key per tab. `sessionStorage` is the exact lifetime we want: a reload
// keeps it (same client, same row), a new tab gets its own, and closing the tab
// abandons it.
const SESSION_KEY_STORAGE = 'presence.sessionKey';

// Comfortably inside the server's 90s freshness window, so one dropped request
// never makes somebody blink offline.
const HEARTBEAT_INTERVAL_MS = 45000;

/**
 * `useUserStore` is a legacy options store in plain JS and infers nothing, so
 * the members used here are named explicitly. `isIdentified` really does return
 * the id rather than a boolean, and the type says so instead of pretending.
 */
interface UserStoreShape {
  isIdentified: number | boolean | undefined;
  token?: string;
}

const sessionKey = (): string => {
  let key = sessionStorage.getItem(SESSION_KEY_STORAGE);

  if (!key) {
    key = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY_STORAGE, key);
  }

  return key;
};

export const usePresenceHeartbeat = () => {
  const UserStore = useUserStore() as unknown as UserStoreShape;
  const GlobalStore = useGlobalStore();
  const OnlineStore = useOnlineStore();
  const route = useRoute();
  const router = useRouter();

  let timer: ReturnType<typeof setInterval> | null = null;
  let inFlight = false;

  const currentPage = (): string => {
    const title = route.meta?.title;
    return String(title || route.name || route.path || '—').slice(0, 191);
  };

  const payload = () => ({
    session_key: sessionKey(),
    app_version: GlobalStore.appVersion,
    platform: `${Platform.is.name ?? 'inconnu'} ${Platform.is.versionNumber ?? ''}`.trim(),
    current_page: currentPage()
  });

  const beat = async (): Promise<void> => {
    // A beat from a signed-out client would create a row nobody can attribute,
    // and the request would 401 anyway.
    if (!UserStore.isIdentified || inFlight) {
      return;
    }

    inFlight = true;
    try {
      const { data } = await axiosApi.post('online/heartbeat', payload());
      // Every client learns the published version here, not just directors —
      // it is what lets one notice it is behind without being told.
      OnlineStore.setServerInfo(data);
    } catch {
      // A missed beat is not worth a notification: the next one is 45s away and
      // the server's freshness window tolerates it.
    } finally {
      inFlight = false;
    }
  };

  /**
   * Fires on `pagehide`, the last moment a browser reliably gives us.
   * `fetch` with `keepalive` rather than `sendBeacon` because the API
   * authenticates with a bearer header and a beacon cannot carry one.
   *
   * Best effort by design — a crash or a dead network never reaches here, which
   * is what the server's freshness window is for.
   */
  const leave = (): void => {
    if (!UserStore.isIdentified) {
      return;
    }

    const url = `${axiosApi.defaults.baseURL ?? '/api'}/online/leave`;
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${UserStore.token}`
      },
      body: JSON.stringify({ session_key: sessionKey() })
    }).catch(() => {
      /* Unloading; there is nobody left to tell. */
    });
  };

  // A backgrounded tab is not somebody at their desk. Beating while hidden is
  // how a phone in a pocket stayed green all afternoon.
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void beat();
      start();
    } else {
      stop();
    }
  };

  const start = (): void => {
    if (timer !== null) {
      return;
    }
    timer = setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS);
  };

  const stop = (): void => {
    if (timer === null) {
      return;
    }
    clearInterval(timer);
    timer = null;
  };

  // Navigation is the one change a watcher sees immediately, so it beats at
  // once rather than waiting up to 45s for the timer.
  watch(() => route.fullPath, () => void beat());

  // Signing in mid-session (no reload) has to register the client, and signing
  // out has to release it. `init()` rides along because the store binds its
  // listeners against a socket that only exists once there is a token: a client
  // that reached this composable before then would otherwise never subscribe.
  watch(
    () => UserStore.isIdentified,
    identified => {
      if (identified) {
        OnlineStore.init();
        void beat();
        start();
      } else {
        stop();
      }
    }
  );

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', leave);

  onBeforeUnmount(() => {
    stop();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', leave);
  });

  if (UserStore.isIdentified) {
    OnlineStore.init();
    // The first beat waits for the router. On a cold load the current route has
    // no name yet, so an early beat reports the raw path — and somebody who
    // lands on home and stays there never navigates to correct it.
    void router.isReady().then(() => void beat());
    start();
  }

  return { beat, leave };
};
